import React, { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, Card, CardContent, CircularProgress, Container, FormControlLabel, Link, Snackbar, Switch, Typography } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { apiListBlocked, apiUnblockUser, apiGetMyProfile, apiUpdateProfile } from '../../services/api';
import { initials } from '../../utils/strings';

const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';

type BlockedUser = { id: string; displayName: string; profileImage: string | null; blockedAt: string };

const Settings: React.FC = () => {
  const { openSettingsDialog } = useCookieConsent();
  const [blocked, setBlocked] = useState<BlockedUser[] | null>(null);
  const [unblockingId, setUnblockingId] = useState<string | null>(null);
  const [blockedError, setBlockedError] = useState<string | null>(null);
  const [unblockMsg, setUnblockMsg] = useState<string | null>(null);
  // null = still loading the current value; the switch is disabled until it arrives.
  const [emailNudges, setEmailNudges] = useState<boolean | null>(null);
  const [savingNudges, setSavingNudges] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiListBlocked();
        if (alive) setBlocked(res.items ?? []);
      } catch {
        if (alive) { setBlocked([]); setBlockedError('Could not load your blocked list.'); }
      }
    })();
    (async () => {
      try {
        const me: any = await apiGetMyProfile();
        const u = me?.data ?? me?.user ?? me;
        if (alive) setEmailNudges(u?.emailNotifyMessages !== false);
      } catch {
        if (alive) setEmailNudges(true); // sensible default; saving still works
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleToggleEmailNudges = async (next: boolean) => {
    if (savingNudges) return;
    const prev = emailNudges;
    setEmailNudges(next);
    setSavingNudges(true);
    try {
      await apiUpdateProfile({ emailNotifyMessages: next } as any);
      setUnblockMsg(next ? 'Email notifications on.' : 'Email notifications off.');
    } catch {
      setEmailNudges(prev); // roll back on failure
      setUnblockMsg('Could not save that setting. Please try again.');
    } finally {
      setSavingNudges(false);
    }
  };

  const handleUnblock = async (id: string) => {
    setUnblockingId(id);
    setBlockedError(null);
    const name = (blocked ?? []).find((u) => u.id === id)?.displayName || 'user';
    try {
      await apiUnblockUser(id);
      setBlocked((prev) => (prev ?? []).filter((u) => u.id !== id));
      setUnblockMsg(`Unblocked ${name}.`);
    } catch {
      setBlockedError('Could not unblock. Please try again.');
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <Container maxWidth="md" disableGutters>
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            <SettingsIcon sx={{ color: 'primary.main' }} />
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your notifications, legal agreements, cookie preferences, and blocked users.
          </Typography>
        </Box>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Legal</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <Link href="/terms" underline="hover" sx={{ color: 'primary.main', fontWeight: 600 }}>Terms of Service</Link>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <Link href="/privacy" underline="hover" sx={{ color: 'primary.main', fontWeight: 600 }}>Privacy Policy</Link>
            </Typography>
            <Typography variant="body2">
              <Link href="/cookies" underline="hover" sx={{ color: 'primary.main', fontWeight: 600 }}>Cookie Policy</Link>
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Notifications</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              We only email you when you're away — at most one nudge every few hours, never one per message.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={emailNudges === true}
                  disabled={emailNudges === null || savingNudges}
                  onChange={(e) => handleToggleEmailNudges(e.target.checked)}
                />
              }
              label="Email me when I have unread messages"
            />
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Cookie Preferences</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Manage optional cookie categories. Essential cookies always remain enabled for login and security.
            </Typography>
            <Button variant="outlined" onClick={openSettingsDialog}>Cookie Settings</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Blocked Users</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You won't see posts from people you block, and neither of you can message the other.
            </Typography>

            {blocked === null ? (
              <Box sx={{ py: 1 }}><CircularProgress size={20} /></Box>
            ) : blocked.length === 0 ? (
              <Typography variant="body2" color="text.secondary">You haven't blocked anyone.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {blocked.map((u) => (
                  <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={u.profileImage || undefined} sx={{ width: 40, height: 40, background: AVATAR_GRAD, color: '#fff', fontWeight: 700 }}>
                      {initials(u.displayName)}
                    </Avatar>
                    <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600 }} noWrap>
                      {u.displayName}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleUnblock(u.id)}
                      disabled={unblockingId === u.id}
                    >
                      {unblockingId === u.id ? 'Unblocking…' : 'Unblock'}
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
            {blockedError && <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{blockedError}</Typography>}
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={!!unblockMsg}
        autoHideDuration={4000}
        onClose={() => setUnblockMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80 } }}
      >
        <Alert severity="success" variant="filled" onClose={() => setUnblockMsg(null)} sx={{ width: '100%' }}>
          {unblockMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;
